import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Clock } from "lucide-react";

function formatActivityDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const EMPTY_PERFORMANCE = Object.freeze({
  items: [],
  totalBroadcasts: 0,
  displayedBroadcasts: 0,
  hasMore: false,
  unavailable: false,
});

/**
 * EmailRecentActivityCard — RECENT-BROADCAST-PERFORMANCE-001.
 *
 * "Recent Broadcast Performance": ONE ROW PER GOVERNED BROADCAST, never an
 * individual recipient/delivery-event/LPE-event row. Retains this
 * component's own pre-existing per-Broadcast row design (campaign/
 * Broadcast identity, activity date, five acquisition-funnel counts) --
 * previously dormant/unreachable dead code (DASH-EMAIL-008B) since no
 * canonical Broadcast attribution existed yet -- now wired to real,
 * canonical-Broadcast-attributed `performance` data from
 * useEmailDashboardData.js's own recentBroadcastPerformance fetch.
 *
 * `performance.unavailable` (never a fabricated row) distinguishes "the
 * fetch itself failed" from a genuine "zero eligible Broadcasts for the
 * selected Period/filters" -- these render two different honest messages,
 * never the same "no data" copy for both.
 */
export default function EmailRecentActivityCard({ performance }) {
  const { items, totalBroadcasts, displayedBroadcasts, hasMore, unavailable } = performance || EMPTY_PERFORMANCE;
  const rows = items || [];

  return (
    <Card className="glass border-white/10" data-testid="email-recent-broadcast-performance-card">
      <CardHeader>
        <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
          <Clock className="h-5 w-5 text-indigo-400" />
          Recent Broadcast Performance
        </CardTitle>
      </CardHeader>
      <CardContent>
        {unavailable ? (
          <p className="text-sm text-slate-400" data-testid="email-recent-broadcast-performance-error">
            Recent Broadcast Performance could not be loaded right now.
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="email-recent-broadcast-performance-empty">
            No recent broadcast performance for the selected filters.
          </p>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div
                key={row.broadcastId}
                className="rounded-lg border border-white/10 bg-white/5 p-3"
                data-testid="email-recent-broadcast-performance-row"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-100">
                    {row.broadcastId}
                    {row.campaignId ? <span className="text-slate-400"> · {row.campaignId}</span> : null}
                    {row.environment ? <span className="text-slate-500"> · {row.environment}</span> : null}
                  </span>
                  <span className="text-xs text-slate-400 shrink-0">{formatActivityDate(row.activityAt)}</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-300 sm:grid-cols-5">
                  <span>Delivered: <span className="text-slate-100">{row.delivered.toLocaleString()}</span></span>
                  <span>LP Views: <span className="text-slate-100">{row.landingPageViews.toLocaleString()}</span></span>
                  <span>Quote Starts: <span className="text-slate-100">{row.quoteStarts.toLocaleString()}</span></span>
                  <span>Quote Completes: <span className="text-slate-100">{row.quoteCompletes.toLocaleString()}</span></span>
                  <span>High Intent: <span className="text-slate-100">{row.highIntentActions.toLocaleString()}</span></span>
                </div>
              </div>
            ))}
            {hasMore && (
              <p className="text-xs text-slate-500" data-testid="email-recent-broadcast-performance-has-more">
                Showing {displayedBroadcasts} of {totalBroadcasts} recent broadcasts
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
